import Phaser from 'phaser';
import gameData from '../gameData';
import ABCyaPhaser from '@abcya/abcya_phaser';
import { checkPixelCollision } from '../ndgmr.Collision-phaser';
import GameMap from './GameMap';
import PlayerPiece from './PlayerPiece';

export default class DrawLayer extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    /** @type {{x: number, y: number}[]} */
    this.pathPoints = null;

    /** @type {PlayerPiece} */
    this.playerSprite = null; // player piece
    /** @type {GameMap} */
    this.collisionMap = null;
    this.isHit = null;
    this.anchorShapes = null;
    this.lastEmptyOccupied = null;
    this.curMapPoint = null;

    if (gameData.gameConfigs[gameData.game].TRACK_CLIP) {
      this.imageLayer = new Phaser.GameObjects.Container(this.scene);
      this.add(this.imageLayer);
    }
    else {

      this.shapeLayer = new Phaser.GameObjects.Graphics(this.scene);
      this.add(this.shapeLayer);
    }
  }

  /**
   * 
   * @param {PlayerPiece} piece 
   * @param {GameMap} map 
   */
  initPath (piece,map) {
    this.pathPoints = [];
    this.isHit = false;
    if (this.shapeLayer) {
      this.shapeLayer.clear();
    }

    if (this.imageLayer) {
      this.imageLayer.removeAll(true);
    }

    this.playerSprite = piece;
    this.collisionMap = map;
  }

  restartPath () {
    this.isHit = false;
  }

  addToPath (newPoint) {
    const mapPt = this.stageToMap(newPoint);

    // check if moved enough
    if (this.pathPoints.length === 0) {
      this.pathPoints.push(mapPt);
      this.curMapPoint = mapPt;
    }
    else {
      const dist = Phaser.Math.Distance.Between(mapPt.x,mapPt.y,this.pathPoints[this.pathPoints.length - 1].x,this.pathPoints[this.pathPoints.length - 1].y);
      const treadDist = 20;

      if (dist >= treadDist) {
        const angleAdjustment = this.calculatePathAngle(mapPt,this.pathPoints[this.pathPoints.length - 1]);
        this.playerSprite.angle = angleAdjustment - 90;

        if (gameData.gameConfigs[gameData.game].TRACK_CLIP) {
          const newTrack = new Phaser.GameObjects.Image(this.scene, 0,0,ABCyaPhaser.Images.DEFAULT_ATLAS, gameData.gameConfigs[gameData.game].TRACK_CLIP);
          newTrack.angle = this.playerSprite.angle;
          newTrack.x = this.playerSprite.x;
          newTrack.y = this.playerSprite.y;
          this.imageLayer.add(newTrack);
        }
        else {
          this.shapeLayer.fillStyle(0x000000);
          this.shapeLayer.fillCircle(mapPt.x,mapPt.y,3);
        }

        this.pathPoints.push(mapPt);
        if (this.pathPoints.length > 50) {
          this.pathPoints.unshift();
        }

        this.curMapPoint = mapPt;
      }
    }

    if (!this.isHit) this.checkGridCollision(mapPt);
  }

  stageToMap (stagePt) {
    return { x: stagePt.x, y: stagePt.y };
  }

  calculatePathAngle (pt1,pt2) {
    const distY = pt2.y - pt1.y;
    const distX = pt2.x - pt1.x;

    const angle = Math.atan2(distY,distX) * 180 / Math.PI;
    return angle;
  }

  checkGridCollision (pt) {
    const carCorners = this.getRotatedSpriteBounds(this.playerSprite.getBounds(),{ x: this.playerSprite.x,y: this.playerSprite.y },this.playerSprite.angle);

    // return if corner is off grid
    // corner names are a bit mixed up because of where zero angle registers with sprite, anchors are clockwise starting with bottom right

    // find all grid blocks the sprite is hitting
    let blocksHit = [];
    const tlBlock = this.collisionMap.getBlockByLocation(carCorners[0]);
    if (tlBlock && tlBlock.blockType === 'empty' && this.lastEmptyOccupied !== tlBlock) this.lastEmptyOccupied = tlBlock;
    if (tlBlock && tlBlock.blockType !== 'empty') blocksHit.push(tlBlock);
    const trBlock = this.collisionMap.getBlockByLocation(carCorners[1]);
    if (trBlock && trBlock.blockType === 'empty' && this.lastEmptyOccupied !== trBlock) this.lastEmptyOccupied = trBlock;
    if (trBlock && blocksHit.indexOf(trBlock) === -1 && trBlock.blockType !== 'empty') blocksHit.push(trBlock);
    const brBlock = this.collisionMap.getBlockByLocation(carCorners[2]);
    if (brBlock && blocksHit.indexOf(brBlock) === -1 && brBlock.blockType !== 'empty') blocksHit.push(brBlock);
    const blBlock = this.collisionMap.getBlockByLocation(carCorners[3]);
    if (blBlock && blocksHit.indexOf(blBlock) === -1 && blBlock.blockType !== 'empty') blocksHit.push(blBlock);

    // compare distance of center of endblock to center of player
    const playerCenterPoint = { x: this.playerSprite.x,y: this.playerSprite.y };
    const endLocation = this.collisionMap.getBlockXY(this.collisionMap.endBlock);
    const gridSize = gameData.gameConfigs[gameData.game].GRID_SIZE;
    const endCenterPoint = { x: endLocation.x + (gridSize / 2),y: endLocation.y + (gridSize / 2) };

    const dist = Phaser.Math.Distance.Between(playerCenterPoint.x,playerCenterPoint.y,endCenterPoint.x,endCenterPoint.y);
    if (dist < gridSize / 2) {
      this.emit('PATH_END_REACHED');
      return;
    }

    // exit if check has been restarted or car has been moved
    if (this.curMapPoint !== pt) return;

    const cornerBlocks = [tlBlock,trBlock,brBlock,blBlock];

    // check for between corners
    const insideBlocks = [];
    let i = 0;
    while (i < cornerBlocks.length + 1) {
      const corner1Index = i;
      const corner2Index = i < cornerBlocks.length - 1 ? i + 1 : 0;

      // corner may be off of screen/grid
      if (cornerBlocks[corner1Index] && cornerBlocks[corner2Index]) {

        const distX = Math.abs(cornerBlocks[corner2Index].columnIndex - cornerBlocks[corner1Index.columnIndex]);
        const distY = Math.abs(cornerBlocks[corner2Index].rowIndex - cornerBlocks[corner1Index.rowIndex]);
        if (distX > 1 || distY > 1) {
          const newColumnIndex = cornerBlocks[corner2Index].columnIndex + (cornerBlocks[corner2Index].columnIndex - cornerBlocks[corner1Index.columnIndex]);
          const newRowIndex = cornerBlocks[corner2Index].rowIndex + (cornerBlocks[corner2Index].rowIndex - cornerBlocks[corner1Index.rowIndex]);
          const newBlock = this.collisionMap.mapGrid.getBlockByCoordinates(newRowIndex,newColumnIndex);
          if (insideBlocks.indexOf(newBlock) === -1 && blocksHit.indexOf(newBlock) === -1 && newBlock.blockType !== 'empty') insideBlocks.push(newBlock);
        }
      }

      i++;
    }

    // exit if check has been restarted or car has been moved
    if (this.curMapPoint !== pt) return;
    blocksHit = blocksHit.concat(insideBlocks);
    i = 0;
    while (i < blocksHit.length) {
      const colliding = checkPixelCollision(this.playerSprite.pieceDisplay,blocksHit[i].sprite,0);
      if (colliding) {
        this.isHit = true;
        const collisionRect = Phaser.Geom.Rectangle.Intersection(this.playerSprite.getBounds(),blocksHit[i].sprite.getBounds());
        colliding.x += collisionRect.x;
        colliding.y += collisionRect.y;
        this.showHit(colliding);
      }

      i++;
    }
  }

  getRotatedSpriteBounds (spriteRect,spritePos,spriteRot) {
    const centerDistance = Phaser.Math.Distance.Between(spriteRect.width / 2,spriteRect.height / 2,spriteRect.width,spriteRect.height);

    // calculate corner angles
    // adding 90 to match car rotation fix, may need to be better
    const cornerAngle = Math.asin((spriteRect.width / 2) / centerDistance) * (180 / Math.PI);
    const tlAngle = -cornerAngle + 90;
    const trAngle = cornerAngle + 90;
    const brAngle = 180 - cornerAngle + 90;
    const blAngle = 180 + cornerAngle + 90;

    const topLeftRotatedPt = getRotatedPoint(tlAngle + spriteRot,centerDistance);
    const topRightRotatedPt = getRotatedPoint(trAngle + spriteRot,centerDistance);
    const bottomRightRotatedPt = getRotatedPoint(brAngle + spriteRot, centerDistance);
    const bottomLeftRotatedPt = getRotatedPoint(blAngle + spriteRot, centerDistance);

    const boundsPoints = [
      { x: topLeftRotatedPt.x + spritePos.x,y: topLeftRotatedPt.y + spritePos.y },
      { x: topRightRotatedPt.x + spritePos.x,y: topRightRotatedPt.y + spritePos.y },
      { x: bottomRightRotatedPt.x + spritePos.x,y: bottomRightRotatedPt.y + spritePos.y },
      { x: bottomLeftRotatedPt.x + spritePos.x,y: bottomLeftRotatedPt.y + spritePos.y }
    ];

    return boundsPoints;
  }

  showHit (collisionInfo) {
    this.emit('PATH_COLLISION', { collisionInfo });
  }
}

function getRotatedPoint(rotateAngle, radius) {
  const px = radius * Math.cos(rotateAngle * Phaser.Math.DEG_TO_RAD);
  const py = radius * Math.sin(rotateAngle * Phaser.Math.DEG_TO_RAD);
  const point = new Phaser.Geom.Point(px,py);
  return point;
}